// Initializes Nosto autocomplete on header search inputs.
// Loaded only when settings.nosto_search_enabled is true.

import {
  autocomplete,
  fromLiquidTemplate
} from 'https://cdn.jsdelivr.net/npm/@nosto/autocomplete@2/dist/liquid/autocomplete.bundle.mjs';

// Custom template using native predictive-search markup
// nosto-dynamic-card is used for product cards, fetching section HTML via Section Rendering API
const TEMPLATE = `
{% assign hasKeywords = response.keywords.hits.length > 0 %}
{% assign hasProducts = response.products.hits.length > 0 %}

{% if hasKeywords or hasProducts %}
<div class="predictive-search__layout">
  <div class="wrapper">
  {% if hasKeywords %}
  <div class="predictive-search__column">
    <div class="predictive-search__group">
      <p class="predictive-search__heading">{{ 'Suggestions' }}</p>
      {% for hit in response.keywords.hits %}
      <div class="predictive-search__item">
        <a class="predictive-search__link" href="/search?q={{ hit.keyword | url_encode }}"
           data-ns-hit="{{ hit | json | escape }}">
          {% if hit._highlight and hit._highlight.keyword %}
            {{ hit._highlight.keyword }}
          {% else %}
            {{ hit.keyword }}
          {% endif %}
        </a>
      </div>
      {% endfor %}
    </div>
  </div>
  {% endif %}

  {% if hasProducts %}
  <div class="predictive-search__column">
    <div class="predictive-search__group">
      <p class="predictive-search__heading">
        {{ 'Products' }}
        {% if response.products.total %}
          <span style="font-weight: 400;">({{ response.products.total }} results)</span>
        {% endif %}
      </p>
      <div class="predictive-search__products__list" data-ns-product-list>
        {% for hit in response.products.hits %}
          {% if hit.handle %}
          <div class="ns-ac-skeleton" data-ns-card-handle="{{ hit.handle }}" data-ns-product-id="{{ hit.productId }}" data-ns-product-url="{{ hit.url }}"></div>
          {% endif %}
        {% endfor %}
      </div>
    </div>
  </div>
  {% endif %}
  </div>
</div>

<div class="predictive-search__actions">
  <button type="submit" class="btn btn--primary">See All</button>
</div>
{% endif %}
`;

// Fetch section HTML and extract the product card (without wrapper)
const sectionCache = {};

async function fetchCardHTML(handle) {
  if (sectionCache[handle]) return sectionCache[handle];
  try {
    // Prefix the market/locale root (e.g. /is-is/) so the card renders in the active
    // market — otherwise its links/currency come back in the default (US) market.
    const root = (window.theme && window.theme.routes && window.theme.routes.root) || '/';
    const resp = await fetch(root + 'products/' + handle + '?section_id=nosto-search-product-card');
    if (!resp.ok) return '';
    const html = await resp.text();
    // Extract inner content — remove the Shopify section wrapper and our nosto-card-section-wrapper
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const inner = doc.querySelector('[data-nosto-card-section]');
    const result = inner ? inner.innerHTML.trim() : '';
    sectionCache[handle] = result;
    return result;
  } catch (e) {
    return '';
  }
}

// Batch: fetch all suggestion cards in ONE request via storefront-search id:
// queries rendered by sections/nosto-search-cards-batch.liquid (previously up
// to 6 individual section fetches per keystroke render). Resolves to a map of
// handle -> card HTML; misses fall back to the per-product fetch.
async function fetchCardsBatch(ids) {
  try {
    const root = (window.theme && window.theme.routes && window.theme.routes.root) || '/';
    const q = ids.map((id) => 'id:' + id).join(' OR ');
    const resp = await fetch(root + 'search?q=' + encodeURIComponent(q) + '&type=product&section_id=nosto-search-cards-batch');
    if (!resp.ok) return {};
    const html = await resp.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const map = {};
    doc.querySelectorAll('[data-batch-card]').forEach((el) => {
      const handle = el.getAttribute('data-handle');
      const out = el.innerHTML.trim();
      if (handle && out) {
        map[handle] = out;
        sectionCache[handle] = out;
      }
    });
    return map;
  } catch (e) {
    return {};
  }
}

// After Liquid template renders, replace skeleton placeholders with real cards
async function loadProductCards(container) {
  const skeletons = Array.from(container.querySelectorAll('[data-ns-card-handle]'));

  const fill = (skeleton, html) => {
    if (!html || !skeleton.isConnected) return;
    const productId = skeleton.getAttribute('data-ns-product-id');
    const productUrl = skeleton.getAttribute('data-ns-product-url');
    // Insert the card HTML and remove the skeleton
    skeleton.insertAdjacentHTML('afterend', html);
    // Transfer product data to the inserted card element for click tracking
    const card = skeleton.nextElementSibling;
    if (card && productId) {
      card.setAttribute('data-ns-product-id', productId);
      card.setAttribute('data-ns-product-url', productUrl || '');
    }
    skeleton.remove();
  };

  // Cached cards fill synchronously; the rest join one batch request.
  const pending = [];
  skeletons.forEach((skeleton) => {
    const handle = skeleton.getAttribute('data-ns-card-handle');
    if (sectionCache[handle]) {
      fill(skeleton, sectionCache[handle]);
    } else {
      pending.push({ skeleton: skeleton, handle: handle, id: skeleton.getAttribute('data-ns-product-id') });
    }
  });
  if (!pending.length) return;

  const withIds = pending.filter((p) => p.id);
  const map = withIds.length ? await fetchCardsBatch(withIds.map((p) => p.id)) : {};
  const misses = [];
  pending.forEach((p) => {
    if (map[p.handle]) fill(p.skeleton, map[p.handle]);
    else misses.push(p);
  });
  await Promise.all(misses.map(async (p) => fill(p.skeleton, await fetchCardHTML(p.handle))));
}

// Derive product handle from URL (Nosto API doesn't return handle)
function addHandle(hit) {
  if (hit && hit.url && !hit.handle) {
    try {
      const path = hit.url.includes('://') ? new URL(hit.url).pathname : hit.url.split('?')[0];
      const m = path.match(/\/products\/([^/?#]+)/);
      if (m) hit.handle = m[1];
    } catch (e) { /* ignore */ }
  }
  return hit;
}

let counter = 0;

function initInput(input) {
  if (input.__nostoInit) return;
  input.__nostoInit = true;

  const scope = input.closest('header-search-popdown, form, [data-popdown]') || document;
  const dropdown = scope.querySelector('[data-nosto-search-results]');
  if (!dropdown) {
    console.warn('[NostoAutocomplete] no dropdown found near input', input);
    return;
  }

  counter += 1;
  if (!input.id)    input.id    = 'nosto-ac-input-' + counter;
  if (!dropdown.id) dropdown.id = 'nosto-ac-dropdown-' + counter;

  const liquidRender = fromLiquidTemplate(TEMPLATE);
  const searchPopdown = input.closest('header-search-popdown, [data-popdown]') || scope;
  const popularSearches = searchPopdown.querySelector('[data-popular-searches]');

  autocomplete({
    inputSelector: '#' + input.id,
    dropdownSelector: '#' + dropdown.id,
    // Custom fetch that routes per keystroke. Nosto's engine is the PRIMARY
    // (personalization, merchandising, A/B, `track: 'autocomplete'`
    // analytics — exactly what the library's own fetch did). The direct
    // cookieless endpoint ONLY answers when Nosto cannot boot: cookie
    // consent unanswered/declined (its bootstrap is consent-gated, so typing
    // used to produce NO suggestions at all), or a stalled boot. The moment
    // Nosto comes up, subsequent keystrokes use the full engine again.
    fetch: async (input) => {
      const empty = { query: { query: input }, response: {} };
      const ns = window.NostoSearch;
      if (!ns) return empty;
      try {
        const denied = ns.consentDenied();
        const bootWait = ns.isNostoReady() || denied === true ? 0 : 2500;
        if (await ns.whenNostoReady(bootWait)) {
          const api = await ns.api();
          const res = await api.search({
            query: input,
            products: {
              fields: ['productId', 'name', 'url', 'imageUrl', 'price', 'listPrice', 'priceCurrencyCode', 'brand', 'available'],
              size: 6
            },
            keywords: {
              fields: ['keyword', '_highlight.keyword'],
              size: 5,
              highlight: { preTag: '<strong>', postTag: '</strong>' }
            }
          }, { track: 'autocomplete', redirect: false });
          const hits = ((res.products && res.products.hits) || []).map((h) => addHandle(h));
          return {
            query: { query: input },
            response: {
              products: { hits: hits, total: (res.products && res.products.total) || 0 },
              keywords: res.keywords || { hits: [] }
            }
          };
        }
        const res = await ns.directSearchAny({
          query: input,
          size: 6,
          keywords: { size: 5, highlight: { preTag: '<strong>', postTag: '</strong>' } }
        });
        return {
          query: { query: input },
          response: {
            products: { hits: res.hits.map((h) => addHandle(h)), total: res.total },
            keywords: { hits: res.keywords, total: res.keywords.length }
          }
        };
      } catch (e) {
        return empty;
      }
    },
    historyEnabled: false,
    render: (container, state) => {
      liquidRender(container, state);
      const hasResults = state?.response?.products?.hits?.length > 0 || state?.response?.keywords?.hits?.length > 0;
      // Toggle empty state vs Nosto results
      dropdown.classList.toggle('ns-has-results', hasResults);
      if (popularSearches) popularSearches.style.display = hasResults ? 'none' : '';
      // Defer card loading to after the library finishes DOM updates
      if (hasResults) setTimeout(() => loadProductCards(dropdown), 0);
    },
    submit: (query) => {
      if (query) {
        window.location.href = '/search?q=' + encodeURIComponent(query);
      }
    }
  });

  // Keyword suggestion click: fire recordSearchSubmit with isKeyword flag before navigation
  dropdown.addEventListener('click', function (e) {
    var link = e.target.closest('.predictive-search__link');
    if (!link) return;

    var keyword = '';
    try {
      var hit = JSON.parse(link.getAttribute('data-ns-hit') || '{}');
      keyword = hit.keyword || '';
    } catch (err) { /* fall back to href */ }
    if (!keyword) {
      var url = new URL(link.href, window.location.origin);
      keyword = url.searchParams.get('q') || '';
    }
    if (!keyword) return;

    // Flag for SERP to read on next page load
    try {
      sessionStorage.setItem('nosto-keyword-click', keyword);
    } catch (err) { /* sessionStorage unavailable, continue */ }

    // Record the keyword-click submit BEFORE navigation
    if (window.NostoSearch && window.NostoSearch.recordSubmit) {
      window.NostoSearch.recordSubmit(keyword, {
        isOrganic: true,
        isKeyword: true
      });
    }
    // No preventDefault — browser navigates via the <a href>.
  });

  // Product card click: fire recordSearchClick('autocomplete', hit) before navigation
  dropdown.addEventListener('click', function (e) {
    var card = e.target.closest('[data-ns-product-id]');
    if (!card) return;
    // Ignore keyword suggestion clicks (handled above)
    if (e.target.closest('.predictive-search__link')) return;

    var productId = card.getAttribute('data-ns-product-id');
    var url = card.getAttribute('data-ns-product-url') || '';
    if (!productId) return;

    try {
      nostojs(function (api) {
        api.recordSearchClick('autocomplete', { productId: productId, url: url });
      });
    } catch (err) {
      console.warn('[NostoAutocomplete] recordSearchClick failed', err);
    }
    // No preventDefault — browser navigates via the <a href>.
  });

  // Restore empty state when input is cleared
  input.addEventListener('input', () => {
    if (!input.value.trim()) {
      dropdown.classList.remove('ns-has-results');
      if (popularSearches) popularSearches.style.display = '';
    }
  });
}

function init() {
  const inputs = document.querySelectorAll('[data-nosto-search-input]');
  inputs.forEach(initInput);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
